import { Box } from "@mui/material";
import { Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ColorPickerField } from "./ColorPickerField";
import { LogoUploadZone } from "./LogoUploadZone";
import { SectionWrapper } from "./SectionWrapper";

interface CoreSectionProps {
  organizationName: string;
  botName?: string | null;
  logoPreview: string | null;
  onLogoUpload: (file: File) => void;
  onLogoRemove: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
}

export const CoreSection = ({
  organizationName,
  botName,
  logoPreview,
  onLogoUpload,
  onLogoRemove,
  control,
}: CoreSectionProps) => {
  const { t } = useTranslation();
  return (
    <SectionWrapper instruction={t("preferences.uiBranding.instructions.core")}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        {/* Organization Badge */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            p: "10px 12px",
            borderRadius: 2,
            background: "linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)",
            border: "1px solid",
            borderColor: "grey.300",
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              background: "linear-gradient(135deg, #1fadb2 0%, #156a70 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontWeight: 600,
              fontSize: "0.8rem",
            }}
          >
            {(botName ?? organizationName).charAt(0).toUpperCase()}
          </Box>
          <Box>
            <Box
              sx={{
                fontSize: "0.65rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "grey.500",
              }}
            >
              {botName
                ? t("preferences.uiBranding.bot")
                : t("preferences.uiBranding.organization")}
            </Box>
            <Box
              sx={{ fontWeight: 600, fontSize: "0.8rem", color: "grey.700" }}
            >
              {botName ? `${botName} · ${organizationName}` : organizationName}
            </Box>
          </Box>
        </Box>

        <LogoUploadZone
          logoPreview={logoPreview}
          onUpload={onLogoUpload}
          onRemove={onLogoRemove}
        />

        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Controller
            name="primaryColor"
            control={control}
            render={({ field }) => (
              <ColorPickerField
                value={field.value}
                onChange={field.onChange}
                label={t("preferences.uiBranding.colorPrimary")}
              />
            )}
          />
          <Controller
            name="secondaryColor"
            control={control}
            render={({ field }) => (
              <ColorPickerField
                value={field.value}
                onChange={field.onChange}
                label={t("preferences.uiBranding.colorSecondary")}
              />
            )}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Controller
            name="sidebarColor"
            control={control}
            render={({ field }) => (
              <ColorPickerField
                value={field.value}
                onChange={field.onChange}
                label={t("preferences.uiBranding.colorSidebar")}
              />
            )}
          />
          <Controller
            name="backgroundColor"
            control={control}
            render={({ field }) => (
              <ColorPickerField
                value={field.value}
                onChange={field.onChange}
                label={t("preferences.uiBranding.colorBackground")}
              />
            )}
          />
        </Box>
      </Box>
    </SectionWrapper>
  );
};
